import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import apiService, { authFetch } from '../../services/apiServices';
import {
  Box, Grid, Card, CardContent, Button, LinearProgress, Typography,
  Alert, Avatar, Chip, Divider, Stack, Tab, Tabs, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer, Paper, Tooltip,
  TextField, MenuItem, CircularProgress, Select, FormControl, InputLabel,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon    from '@mui/icons-material/ArrowBack';
import PrintIcon        from '@mui/icons-material/Print';
import EmailIcon        from '@mui/icons-material/Email';
import PersonIcon       from '@mui/icons-material/Person';
import BadgeIcon        from '@mui/icons-material/Badge';
import BusinessIcon     from '@mui/icons-material/Business';
import PhoneIcon        from '@mui/icons-material/Phone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BeachAccessIcon  from '@mui/icons-material/BeachAccess';
import TrendingUpIcon   from '@mui/icons-material/TrendingUp';
import WorkIcon         from '@mui/icons-material/Work';
import AccessTimeIcon   from '@mui/icons-material/AccessTime';
import HomeWorkIcon     from '@mui/icons-material/HomeWork';
import SendReportEmailDialog from '../common/SendReportEmailDialog';

const ANNUAL_ID = 1;

// ── Attendance helpers ────────────────────────────────────────────────────────
const fmtHHMM = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  return `${Math.floor(m/60)}:${('0'+(m%60)).slice(-2)}`;
};
const fmtClock = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return '—';
  return `${('0'+d.getHours()).slice(-2)}:${('0'+d.getMinutes()).slice(-2)}`;
};
const WEEK_DAY_KEYS = ['report.sun','report.mon','report.tue','report.wed','report.thu','report.fri','report.sat'];

// ── Brand palette — aligned to the app theme (teal primary #3E8FA3) ───────────
const BRAND      = '#3E8FA3'; // theme primary.main
const BRAND_DARK = '#256878'; // theme primary.dark
const BRAND_DEEP = '#1E4E5A'; // deep teal — table headers, totals, emphasis
const BRAND_SOFT = '#E6F4F8'; // theme primary.soft — soft fills
const POS_GREEN  = '#059669'; // gross / positive
const NEG_RED    = '#DC2626'; // taxes / negative
const ROW_ALT    = '#F6FAFB'; // zebra stripe
const LEAVE_COLORS = [BRAND, '#D97706', '#16A34A', '#9333EA', '#0891B2', '#DC2626', '#64748B'];
const MONTH_KEYS = ['', 'report.january','report.february','report.march','report.april','report.may_full','report.june','report.july','report.august','report.september','report.october','report.november','report.december'];

const fmt = (v) => new Intl.NumberFormat('sq-AL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(Number(v) || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('sq-AL', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const sum = (arr, key) => (arr || []).reduce((a, b) => a + (Number(b?.[key]) || 0), 0);
const initials = (fn = '', ln = '') => `${fn[0] || ''}${ln[0] || ''}`.toUpperCase() || '?';
// Round to 2 decimals (kills float drift like 2.5999999…)
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
// Display a day/number count: max 2 decimals, no trailing zeros (2.6 → "2.6", 20 → "20")
const fmtNum = (n) => String(round2(n));

// ── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, color = BRAND, sub }) {
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', height: '100%', borderRadius: 2 }}>
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction="row" alignItems="flex-start" spacing={1.5}>
          <Box sx={{ bgcolor: alpha(color, 0.1), color, p: 1, borderRadius: 1.5, display: 'flex' }}>
            {icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
              {label}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: '1.1rem', color }}>
              {value}
            </Typography>
            {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Info row ──────────────────────────────────────────────────────────────────
function InfoRow({ icon, label, value }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.75 }}>
      <Box sx={{ color: 'text.secondary', display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{value || '—'}</Typography>
      </Box>
    </Stack>
  );
}

// ── Leave row ─────────────────────────────────────────────────────────────────
// `allowed`/`remaining` arrive from the shared balance service; null means the
// type has no yearly cap, so there is nothing to run out of.
function LeaveRow({ label, used, allowed, allowedLabel, remaining: rem, color }) {
  const pct = allowed > 0 ? Math.min((used / allowed) * 100, 100) : 0;
  const remaining = rem === null || rem === undefined ? '—' : round2(rem);
  return (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 500, py: 1.2 }}>{label}</TableCell>
      <TableCell align="right">{allowed > 0 ? allowed : (allowedLabel || '—')}</TableCell>
      <TableCell align="right">
        <Chip label={used} size="small" color={used > 0 ? 'default' : 'default'} sx={{ fontWeight: 700, minWidth: 36 }} />
      </TableCell>
      <TableCell align="right">
        <Chip label={remaining} size="small"
          color={typeof remaining === 'number' && remaining <= 3 ? 'error' : 'success'}
          variant={remaining === 0 ? 'filled' : 'outlined'}
          sx={{ fontWeight: 700, minWidth: 36 }}
        />
      </TableCell>
      <TableCell sx={{ width: 120 }}>
        <Box sx={{ bgcolor: alpha(color, 0.12), borderRadius: 2, height: 6, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: color, height: '100%', width: `${pct}%`, borderRadius: 2, transition: 'width .5s' }} />
        </Box>
      </TableCell>
    </TableRow>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function EmployeeCard() {
  const { t } = useTranslation();
  const WEEK_DAYS = useMemo(() => WEEK_DAY_KEYS.map(k => t(k)), [t]);
  const MONTHS = useMemo(() => ['', ...MONTH_KEYS.slice(1).map(k => t(k))], [t]);
  const { id } = useParams();
  const [loader,       setLoader]       = useState(true);
  const [employeeCard, setEmployeeCard] = useState(null);
  const [error,        setError]        = useState(null);
  const [tab,          setTab]          = useState(0);
  const [emailOpen,    setEmailOpen]    = useState(false);
  const [settings,     setSettings]     = useState({});
  // Leave tab — figures from the shared balance service, per leave type.
  const [leaveYear,  setLeaveYear]  = useState(new Date().getFullYear());
  const [leaveRow,   setLeaveRow]   = useState(null);
  const [leaveTypes, setLeaveTypes] = useState([]);

  // Attendance tab state
  const now = new Date();
  const [attYear,  setAttYear]  = useState(now.getFullYear());
  const [attMonth, setAttMonth] = useState(now.getMonth() + 1);
  const [attData,  setAttData]  = useState([]);
  const [attLoading, setAttLoading] = useState(false);

  // ── fiscal calculation ─────────────────────────────────────────────────────
  const calcTaxes = useCallback((salary, ranges) => {
    let total = 0;
    for (const r of (ranges || [])) {
      const from = Number(r.From ?? r.MinAmount ?? r.from ?? 0);
      const toRaw = r.To ?? r.MaxAmount ?? r.to ?? null;
      const to = toRaw != null ? Number(toRaw) : null;
      const rate = Number(r.Tax ?? r.Percent ?? r.percent ?? 0) / 100;
      if (to === null || to === 0) { if (salary > from) total += (salary - from) * rate; }
      else if (salary > to) total += (to - from) * rate;
      else if (salary > from) total += (salary - from) * rate;
    }
    return total;
  }, []);

  const enrichSalaries = useCallback((ranges, obj, fp) => {
    const empRate = Number(fp?.EmployeeTax ?? fp?.EmployeeContribution ?? 5) / 100;
    const erRate  = Number(fp?.EmployerTax  ?? fp?.EmployerContribution  ?? 5) / 100;
    const ec = { ...obj, EmployeeTax: empRate * 100, EmployerTax: erRate * 100 };
    (ec.salaries || []).forEach(s => {
      const gross = Number(s.Salary || s.GrossSalary || 0);
      s.employeeTaxFee  = +(empRate * gross).toFixed(2);
      s.employerTaxFee  = +(erRate  * gross).toFixed(2);
      s.contributions   = +((empRate + erRate) * gross).toFixed(2);
      const taxable     = gross - empRate * gross;
      s.taxes           = +calcTaxes(taxable, ranges).toFixed(2);
      s.neto            = +(taxable - s.taxes + Number(s.Bonus || 0)).toFixed(2);
      s.total           = s.neto;
    });
    setEmployeeCard(ec);
    setLoader(false);
  }, [calcTaxes]);

  const loadCard = useCallback((workerId) => {
    if (!workerId) { setError(t('report.workerIdMissing')); setLoader(false); return; }
    apiService().query('employeeCard', `id=${workerId}`)
      .then(res => {
        if (!res?.success || !res.data?.length) { setError(t('report.workerNotFound')); setLoader(false); return; }
        const obj = res.data[res.data.length - 1];
          // salaries
          apiService().query('getWorkerSalary', `workerId=${obj.Id || obj.id}`).then(sr => {
            obj.salaries = sr?.success ? sr.data || [] : [];
            // fiscal
            apiService().get('fiscalPolitics').then(fp => {
              const policy = fp?.success && fp.data?.length ? fp.data[0] : {};
              const fpId = policy?.Id || policy?.id;
              if (fpId) {
                apiService().query('getFiscalpoliticsDetails', `FiscalPoliticsId=${fpId}`)
                  .then(dr => enrichSalaries(dr?.success ? dr.data || [] : [], obj, policy))
                  .catch(() => enrichSalaries([], obj, policy));
              } else enrichSalaries([], obj, policy);
            }).catch(() => enrichSalaries([], obj, {}));
          }).catch(() => { obj.salaries = []; enrichSalaries([], obj, {}); });
      })
      .catch(() => { setError(t('report.loadingError')); setLoader(false); });
  }, [enrichSalaries]);

  useEffect(() => {
    apiService().get('settings').then(r => { if (r?.success && r.data?.[0]) setSettings(r.data[0]); });
    loadCard(id);
  }, [id, loadCard]);

  // status=all: the card of someone who already left must still show their year.
  useEffect(() => {
    if (!id) return;
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`/api/reports/leave-balance?workerId=${id}&year=${leaveYear}&status=all&include=types`);
        const j = await res.json();
        if (!alive) return;
        setLeaveRow(j?.data?.rows?.[0] || null);
        setLeaveTypes(j?.data?.leaveTypes || []);
      } catch {
        if (alive) { setLeaveRow(null); setLeaveTypes([]); }
      }
    })();
    return () => { alive = false; };
  }, [id, leaveYear]);

  useEffect(() => {
    if (tab !== 3 || !id) return;
    const mm = String(attMonth).padStart(2,'0');
    const from = `${attYear}-${mm}-01`;
    const last = new Date(attYear, attMonth, 0).getDate();
    const to   = `${attYear}-${mm}-${last}`;
    setAttLoading(true);
    apiService().query('getWorkerAttendance', `workerId=${id}&from=${from}&to=${to}`)
      .then(r => { setAttData(r?.success ? r.data || [] : []); })
      .catch(() => setAttData([]))
      .finally(() => setAttLoading(false));
  }, [tab, id, attYear, attMonth]);

  // ── derived data ───────────────────────────────────────────────────────────
  const ec       = employeeCard;
  const fullName = ec ? `${ec.FirstName || ''} ${ec.LastName || ''}`.trim() : '';
  const sals     = ec?.salaries || [];

  // Year filter
  const availableYears = [...new Set(sals.map(s => Number(s.Year || s.year)).filter(Boolean))].sort((a, b) => b - a);
  const [filterYear, setFilterYear] = useState('all');
  const filteredSals = filterYear === 'all' ? sals : sals.filter(s => Number(s.Year || s.year) === Number(filterYear));

  const totalNeto    = sum(filteredSals, 'neto');
  const totalGross   = sum(filteredSals, 'Salary');
  const totalTaxes   = sum(filteredSals, 'taxes');
  const totalContrib = sum(filteredSals, 'contributions');

  // Annual always; another type only when it has a cap or was used this year.
  const LEAVE_POLICY = leaveTypes
    .filter((lt) => {
      const b = leaveRow?.byType?.[lt.Id];
      return Number(lt.Id) === ANNUAL_ID || (b && (b.approvedDays > 0 || b.pendingDays > 0 || (b.hasEntitlement && b.entitlementDays > 0)));
    })
    .reduce((m, lt, i) => {
      const b = leaveRow?.byType?.[lt.Id] || {};
      m[lt.Id] = {
        label: lt.Name,
        used: round2(b.approvedDays),
        allowed: b.hasEntitlement ? round2(b.entitlementDays) : 0,
        allowedLabel: b.allowanceLabel || lt.Rule?.label || '',
        remaining: b.hasEntitlement ? b.remainingDays : null,
        color: LEAVE_COLORS[i % LEAVE_COLORS.length],
      };
      return m;
    }, {});

  // ── PDF print ──────────────────────────────────────────────────────────────
  const printPdf = () => {
    const now = new Date().toLocaleDateString('sq-AL', { day: '2-digit', month: 'long', year: 'numeric' });
    const company = settings?.CompanyName || 'Kompania';

    const salaryRows = sals.map((s, i) => {
      const bg = i % 2 === 0 ? '#fff' : '#F8FAFF';
      return `<tr style="background:${bg}">
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5">${s.ListName || MONTHS[Number(s.Month || s.month)] || `${t('report.month')} ${s.listId || s.ListId || i+1}`}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right">${fmt(s.Salary)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right">${fmt(s.employeeTaxFee)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right">${fmt(s.employerTaxFee)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right">${fmt(s.contributions)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right">${fmt(s.taxes)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right;font-weight:700;color:#256878">${fmt(s.neto)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right">${fmt(s.Bonus)}</td>
      </tr>`;
    }).join('');

    const leaveRows = Object.entries(LEAVE_POLICY).map(([, p]) => {
      const rem = p.remaining === null || p.remaining === undefined ? '—' : round2(p.remaining);
      const pct = p.allowed > 0 ? Math.min((p.used / p.allowed) * 100, 100).toFixed(0) : 0;
      return `<tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5">${p.label}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right">${p.allowed || p.allowedLabel || '—'}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right">${p.used}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5;text-align:right"><strong>${rem}</strong></td>
        <td style="padding:6px 8px;border-bottom:1px solid #e8edf5">
          <div style="background:#e8edf5;border-radius:4px;height:6px;overflow:hidden">
            <div style="background:${p.color};height:100%;width:${pct}%;border-radius:4px"></div>
          </div>
        </td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="sq"><head><meta charset="UTF-8"><title>Kartela — ${fullName}</title>
<style>
  @page { size: A4 portrait; margin: 14mm 12mm 16mm; }
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Segoe UI',Arial,sans-serif;font-size:9pt;color:#1a1a2e;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .hero{background:linear-gradient(135deg,#1E4E5A 0%,#3E8FA3 100%);color:white;padding:18px 22px;border-radius:8px 8px 0 0;display:flex;align-items:center;gap:16px;margin-bottom:0}
  .avatar{width:56px;height:56px;border-radius:50%;background:rgba(255,255,255,.2);display:flex;align-items:center;justify-content:center;font-size:22px;font-weight:800;flex-shrink:0;border:2px solid rgba(255,255,255,.4)}
  .hero-info{flex:1}
  .hero-company{font-size:7.5pt;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;opacity:.7;margin-bottom:2px}
  .hero-name{font-size:16pt;font-weight:800;line-height:1.1}
  .hero-pos{font-size:9pt;opacity:.8;margin-top:2px}
  .hero-date{text-align:right;font-size:7.5pt;opacity:.7;line-height:1.8}
  .meta-bar{background:#E6F4F8;border:1px solid #BAE0EA;border-top:none;padding:8px 18px;display:flex;gap:20px;flex-wrap:wrap;margin-bottom:14px;border-radius:0 0 6px 6px}
  .meta-item{font-size:8pt;color:#256878;font-weight:600}
  .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:14px}
  .stat-card{border:1px solid #e3eaee;border-radius:6px;padding:10px 12px}
  .stat-label{font-size:7pt;color:#6b7280;font-weight:600;letter-spacing:.4px;text-transform:uppercase;margin-bottom:2px}
  .stat-value{font-size:13pt;font-weight:800;color:#256878}
  h3{font-size:10pt;font-weight:700;color:#256878;margin:14px 0 6px;border-bottom:2px solid #E6F4F8;padding-bottom:4px}
  table{width:100%;border-collapse:collapse;font-size:8pt;margin-bottom:10px}
  thead tr{background:#1E4E5A;color:white}
  thead th{padding:7px 8px;font-weight:700;font-size:7.5pt;text-align:left;white-space:nowrap}
  thead th:not(:first-child){text-align:right}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:14px}
  .info-item{border:1px solid #e8edf5;border-radius:5px;padding:8px 10px}
  .info-label{font-size:7pt;color:#6b7280;font-weight:600;text-transform:uppercase;letter-spacing:.3px}
  .info-value{font-size:9pt;font-weight:600;margin-top:1px}
  .footer{border-top:2px solid #256878;padding-top:8px;margin-top:10px;display:flex;justify-content:space-between;font-size:7.5pt;color:#9ca3af}
  .footer-brand{font-weight:700;color:#256878}
  .no-print{display:block}
  @media print{.no-print{display:none!important}}
  .print-bar{background:#f1f5f9;border:1px solid #e2e8f0;border-radius:8px;padding:10px 14px;margin-bottom:14px;display:flex;gap:8px;align-items:center}
  .btn{border:none;border-radius:6px;padding:8px 18px;font-size:10pt;font-weight:700;cursor:pointer;font-family:'Segoe UI',Arial,sans-serif}
  .btn-primary{background:#3E8FA3;color:white}.btn-secondary{background:white;color:#555;border:1px solid #d1d5db}
</style>
</head><body>
<div class="no-print print-bar">
  <button class="btn btn-primary" onclick="window.print()">🖨️ &nbsp;Printo / Ruaj si PDF</button>
  <button class="btn btn-secondary" onclick="window.close()">✕ &nbsp;Mbyll</button>
  <span style="font-size:8.5pt;color:#64748b">File → Print → Destination: Save as PDF</span>
</div>

<div class="hero">
  <div class="avatar">${initials(ec?.FirstName, ec?.LastName)}</div>
  <div class="hero-info">
    <div class="hero-company">${company}</div>
    <div class="hero-name">${fullName}</div>
    <div class="hero-pos">${ec?.Position || ec?.WorkType || '—'} &nbsp;·&nbsp; ${ec?.DepartmentName || ec?.Department || '—'}</div>
  </div>
  <div class="hero-date"><div style="font-size:20pt;font-weight:800;opacity:.2">HR</div>${t('report.employeeCardTitle')}<br/>${t('report.generated')}: ${now}</div>
</div>
<div class="meta-bar">
  <span class="meta-item">📋 NID: ${ec?.PersonalId || ec?.PersonalNumber || '—'}</span>
  <span class="meta-item">🗓️ ${t('report.employed')}: ${fmtDate(ec?.HireDate || ec?.StartDate)}</span>
  <span class="meta-item">📧 ${ec?.Email || '—'}</span>
  <span class="meta-item">📞 ${ec?.Phone || '—'}</span>
</div>

<div class="stats">
  <div class="stat-card"><div class="stat-label">${t('report.netoYTD')}</div><div class="stat-value">${fmt(totalNeto)}</div></div>
  <div class="stat-card"><div class="stat-label">${t('report.brutoYTD')}</div><div class="stat-value" style="color:#059669">${fmt(totalGross)}</div></div>
  <div class="stat-card"><div class="stat-label">${t('report.taxYTD')}</div><div class="stat-value" style="color:#DC2626">${fmt(totalTaxes)}</div></div>
  <div class="stat-card"><div class="stat-label">${t('report.registeredMonths')}</div><div class="stat-value" style="color:#7C3AED">${sals.length}</div></div>
</div>

<h3>📊 ${t('report.salaryHistory')}</h3>
<table>
  <thead><tr>
    <th>${t('report.month')}</th><th>${t('report.gross')} (€)</th><th>${t('report.workerContrib')}</th><th>${t('report.employerContrib')}</th><th>${t('report.contributions')}</th><th>${t('report.tax')}</th><th>${t('report.net')} (€)</th><th>${t('report.bonus')}</th>
  </tr></thead>
  <tbody>
    ${salaryRows || `<tr><td colspan="8" style="text-align:center;padding:14px;color:#9ca3af">${t('report.noSalaryData')}</td></tr>`}
    <tr style="background:#1E4E5A;color:white;font-weight:700">
      <td style="padding:7px 8px">${t('report.total')}</td>
      <td style="padding:7px 8px;text-align:right">${fmt(totalGross)}</td>
      <td style="padding:7px 8px;text-align:right">${fmt(sum(sals,'employeeTaxFee'))}</td>
      <td style="padding:7px 8px;text-align:right">${fmt(sum(sals,'employerTaxFee'))}</td>
      <td style="padding:7px 8px;text-align:right">${fmt(totalContrib)}</td>
      <td style="padding:7px 8px;text-align:right">${fmt(totalTaxes)}</td>
      <td style="padding:7px 8px;text-align:right">${fmt(totalNeto)}</td>
      <td style="padding:7px 8px;text-align:right">${fmt(sum(sals,'Bonus'))}</td>
    </tr>
  </tbody>
</table>

<h3>🏖️ ${t('report.leaves')}</h3>
<table>
  <thead><tr><th>${t('report.leaveType')}</th><th>${t('report.allowed')}</th><th>${t('report.used')}</th><th>${t('report.remaining')}</th><th style="text-align:left;width:100px">${t('report.progress')}</th></tr></thead>
  <tbody>${leaveRows}</tbody>
</table>

<div class="footer">
  <div><span class="footer-brand">Nexuma</span> &nbsp;·&nbsp; ${t('report.confidentialInternal')}</div>
  <div>${t('report.signature')}: _____________________ &nbsp;&nbsp; ${t('report.date')}: _______________</div>
</div>
</body></html>`;

    const win = window.open('', '_blank', 'width=1000,height=800,scrollbars=yes');
    if (!win) { alert(t('report.allowPopups')); return; }
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
  };

  // ── render ─────────────────────────────────────────────────────────────────
  if (loader) return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button component={Link} to="/home/reports/employees-cards" startIcon={<ArrowBackIcon />} variant="outlined" size="small">Kthehu</Button>
      </Box>
      <LinearProgress />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('report.loadingWorkerCard')}</Typography>
    </Box>
  );

  if (error) return (
    <Box>
      <Button component={Link} to="/home/reports/employees-cards" startIcon={<ArrowBackIcon />} variant="outlined" size="small" sx={{ mb: 2 }}>Kthehu</Button>
      <Alert severity="error">{error}</Alert>
    </Box>
  );

  if (!ec) return null;

  return (
    <Box sx={{ pb: 4 }}>
      {/* ── HERO CARD ─────────────────────────────────────────── */}
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', mb: 3 }}>
        {/* Gradient banner */}
        <Box sx={{ background: `linear-gradient(135deg, ${BRAND_DEEP} 0%, ${BRAND} 100%)`, px: 3, pt: 3, pb: 5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ width: 72, height: 72, bgcolor: 'rgba(255,255,255,0.2)', fontSize: 28, fontWeight: 800, border: '3px solid rgba(255,255,255,0.35)' }}>
                {initials(ec.FirstName, ec.LastName)}
              </Avatar>
              <Box>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', fontSize: '0.68rem' }}>
                  {settings?.CompanyName || 'Kompania'}
                </Typography>
                <Typography variant="h4" sx={{ color: 'white', fontWeight: 800, lineHeight: 1.15, mt: 0.3 }}>{fullName}</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap">
                  {ec.Position && <Chip label={ec.Position} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600, fontSize: '0.72rem' }} />}
                  {(ec.DepartmentName || ec.Department) && <Chip label={ec.DepartmentName || ec.Department} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'white', fontWeight: 600, fontSize: '0.72rem' }} />}
                  {ec.Active !== false && <Chip label={t('report.active')} size="small" sx={{ bgcolor: '#16A34A', color: 'white', fontWeight: 700, fontSize: '0.72rem' }} />}
                </Stack>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" size="small" startIcon={<EmailIcon />}
                onClick={() => setEmailOpen(true)}
                sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                Email
              </Button>
              <Button variant="contained" size="small" startIcon={<PrintIcon />}
                onClick={printPdf}
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(4px)', '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' }, fontWeight: 700 }}>
                PDF
              </Button>
              <Button component={Link} to="/home/reports/employees-cards" size="small" startIcon={<ArrowBackIcon />}
                sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
                Kthehu
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Pull-up stats strip */}
        <Box sx={{ px: 3, mt: -2.5 }}>
          <Grid container spacing={2}>
            {[
              { icon: <AccountBalanceWalletIcon />, label: t('report.netoYTD'), value: fmt(totalNeto), color: BRAND },
              { icon: <TrendingUpIcon />, label: t('report.brutoYTD'), value: fmt(totalGross), color: POS_GREEN },
              { icon: <BeachAccessIcon />, label: t('report.totalLeaves'), value: `${fmtNum(ec.days)} ${t('report.days')}`, color: '#D97706' },
              { icon: <WorkIcon />, label: t('report.salaryMonths'), value: `${sals.length} ${t('report.months')}`, color: '#7C3AED' },
            ].map((s) => (
              <Grid item xs={12} sm={6} md={3} key={s.label}>
                <StatCard {...s} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Quick info pills */}
        <Box sx={{ px: 3, pb: 2, pt: 2.5 }}>
          <Stack direction="row" spacing={3} flexWrap="wrap" gap={1}>
            {[
              { icon: <BadgeIcon sx={{ fontSize: 14 }} />, label: ec.PersonalId || ec.PersonalNumber },
                  { icon: <CalendarMonthIcon sx={{ fontSize: 14 }} />, label: `${t('report.employed')}: ${fmtDate(ec.HireDate || ec.StartDate)}` },
              { icon: <EmailIcon sx={{ fontSize: 14 }} />, label: ec.Email },
              { icon: <PhoneIcon sx={{ fontSize: 14 }} />, label: ec.Phone },
              { icon: <BusinessIcon sx={{ fontSize: 14 }} />, label: ec.BranchName || ec.Branch },
            ].filter(p => p.label).map((p, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={0.5}>
                <Box sx={{ color: 'text.secondary' }}>{p.icon}</Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>{p.label}</Typography>
              </Stack>
            ))}
          </Stack>
        </Box>
      </Card>

      {/* ── TABS CONTENT ──────────────────────────────────────── */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider', '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48 } }}>
        <Tab icon={<AccountBalanceWalletIcon fontSize="small" />} iconPosition="start" label={t('report.salaryHistory')} />
        <Tab icon={<BeachAccessIcon fontSize="small" />} iconPosition="start" label={t('report.leaves')} />
        <Tab icon={<PersonIcon fontSize="small" />} iconPosition="start" label={t('report.fullProfile')} />
        <Tab icon={<AccessTimeIcon fontSize="small" />} iconPosition="start" label={t('report.presence')} />
      </Tabs>

      {/* ── TAB 0: Salary history ──────────────────────────────── */}
      {tab === 0 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          {/* Year filter toolbar */}
          {availableYears.length > 0 && (
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{t('report.filterByYear')}:</Typography>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={t('report.all')}
                  size="small"
                  onClick={() => setFilterYear('all')}
                  color={filterYear === 'all' ? 'primary' : 'default'}
                  variant={filterYear === 'all' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 600 }}
                />
                {availableYears.map(y => (
                  <Chip
                    key={y}
                    label={y}
                    size="small"
                    onClick={() => setFilterYear(String(y))}
                    color={filterYear === String(y) ? 'primary' : 'default'}
                    variant={filterYear === String(y) ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {filteredSals.length} {t('report.months')}
              </Typography>
            </Box>
          )}
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            {filteredSals.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">{t('report.noSalaryDataForPeriod')}</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[t('report.month'), t('report.grossEur'), t('report.workerContrib'), t('report.employerContrib'), t('report.contributions'), t('report.tax'), t('report.netEur'), t('report.bonus')].map(h => (
                        <TableCell
                          key={h}
                          align={h === t('report.month') ? 'left' : 'right'}
                          sx={{
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            bgcolor: BRAND_DEEP,
                            color: '#fff !important',
                            fontSize: '0.72rem',
                            letterSpacing: 0.4,
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSals.map((s, i) => (
                      <TableRow key={i} hover sx={{ '&:nth-of-type(even)': { bgcolor: ROW_ALT } }}>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {s.ListName || MONTHS[Number(s.Month || s.month)] || `${t('report.month')} ${s.listId || s.ListId || '—'}`}
                          {(s.Year || s.year) && filterYear === 'all' && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                              {s.Year || s.year}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{fmt(s.Salary)}</TableCell>
                        <TableCell align="right">{fmt(s.employeeTaxFee)}</TableCell>
                        <TableCell align="right">{fmt(s.employerTaxFee)}</TableCell>
                        <TableCell align="right">{fmt(s.contributions)}</TableCell>
                        <TableCell align="right" sx={{ color: NEG_RED }}>{fmt(s.taxes)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: BRAND_DEEP }}>{fmt(s.neto)}</TableCell>
                        <TableCell align="right" sx={{ color: POS_GREEN }}>{Number(s.Bonus) ? fmt(s.Bonus) : '—'}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: BRAND_SOFT, borderTop: `2px solid ${BRAND_DEEP}`, '&:hover': { bgcolor: BRAND_SOFT } }}>
                      <TableCell sx={{ fontWeight: 800, color: BRAND_DEEP }}>
                        {t('report.total')} {filterYear !== 'all' ? filterYear : ''}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totalGross)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(sum(filteredSals,'employeeTaxFee'))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(sum(filteredSals,'employerTaxFee'))}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totalContrib)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: NEG_RED }}>{fmt(totalTaxes)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: BRAND_DEEP, fontSize: '0.95rem' }}>{fmt(totalNeto)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: POS_GREEN }}>{fmt(sum(filteredSals,'Bonus'))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── TAB 1: Leave summary ──────────────────────────────── */}
      {tab === 1 && (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25 }}>
              <Typography variant="caption" color="text.secondary">
                Vetëm kërkesat e aprovuara · në pritje: {round2(leaveRow?.pendingDays)} ditë vjetore
              </Typography>
              <TextField select size="small" label={t('report.year')} value={leaveYear}
                onChange={(e) => setLeaveYear(Number(e.target.value))} sx={{ width: 110 }}>
                {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            </Stack>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                  {[t('report.leaveType'), t('report.allowed'), t('report.used'), t('report.remaining'), t('report.progress')].map((h, hi) => (
                        <TableCell key={h} align={hi === 0 || hi === 4 ? 'left' : 'right'} sx={{ fontWeight: 700, bgcolor: BRAND_DEEP, color: '#fff !important', fontSize: '0.72rem', letterSpacing: 0.4 }}>{h}</TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(LEAVE_POLICY).map(([k, p]) => (
                    <LeaveRow key={k} {...p} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ── TAB 2: Full profile ────────────────────────────────── */}
      {tab === 2 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND, mb: 1.5 }}>{t('report.personalData')}</Typography>
                <Stack divider={<Divider flexItem />} spacing={0}>
                  <InfoRow icon={<PersonIcon fontSize="small" />}       label={t('report.fullName')}   value={fullName} />
                  <InfoRow icon={<BadgeIcon fontSize="small" />}        label={t('report.personalNid')} value={ec.PersonalId || ec.PersonalNumber} />
                  <InfoRow icon={<CalendarMonthIcon fontSize="small"/>} label={t('report.birthDate')}   value={fmtDate(ec.DateOfBirth)} />
                  <InfoRow icon={<EmailIcon fontSize="small"/>}         label={t('report.email')}        value={ec.Email} />
                  <InfoRow icon={<PhoneIcon fontSize="small"/>}         label={t('report.phone')}        value={ec.Phone} />
                  <InfoRow icon={<BusinessIcon fontSize="small"/>}      label={t('report.address')}      value={ec.Address} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND, mb: 1.5 }}>{t('report.workData')}</Typography>
                <Stack divider={<Divider flexItem />} spacing={0}>
                  <InfoRow icon={<WorkIcon fontSize="small"/>}          label={t('report.position')}       value={ec.Position || ec.WorkType} />
                  <InfoRow icon={<BusinessIcon fontSize="small"/>}      label={t('report.department')}     value={ec.DepartmentName || ec.Department} />
                  <InfoRow icon={<BusinessIcon fontSize="small"/>}      label={t('report.branch')}         value={ec.BranchName || ec.Branch} />
                  <InfoRow icon={<CalendarMonthIcon fontSize="small"/>} label={t('report.hireDate')}       value={fmtDate(ec.HireDate || ec.StartDate)} />
                  <InfoRow icon={<BadgeIcon fontSize="small"/>}         label={t('report.contractType')}   value={ec.ContractType || ec.WorkType} />
                  <InfoRow icon={<AccountBalanceWalletIcon fontSize="small"/>} label={t('report.bankAccount')} value={ec.BankAccount} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          {/* Contract dates */}
          {(ec.StartDate || ec.Contract?.StartDate) && (
            <Grid item xs={12}>
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND, mb: 1 }}>{t('report.contract')}</Typography>
                  <Stack direction="row" spacing={4}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('report.startDate')}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtDate(ec.StartDate || ec.Contract?.StartDate)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('report.endDate')}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {ec.EndDate || ec.Contract?.EndDate ? fmtDate(ec.EndDate || ec.Contract?.EndDate) : <Chip label={t('report.noExpiry')} size="small" color="success" />}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">{t('report.atkRegistered')}</Typography>
                      <Typography variant="body2">
                        <Chip size="small" label={ec.ATKRegistered ? t('report.yes') : t('report.no')} color={ec.ATKRegistered ? 'success' : 'error'} />
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      )}

      {/* ── TAB 3: Attendance / Prezenca ──────────────────────── */}
      {tab === 3 && (() => {
        const totalWork = attData.reduce((s, r) => s + (r.workMin || 0), 0);
        const totalBreak = attData.reduce((s, r) => s + (r.breakMin || 0), 0);
        const workedDays = attData.filter(r => r.checkIn).length;
        const remoteDays = attData.filter(r => r.location === 'remote' && r.checkIn).length;
        return (
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            {/* Toolbar */}
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <AccessTimeIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="body2" fontWeight={700} color="text.secondary">{t('report.presence')}:</Typography>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select value={attMonth} onChange={e => setAttMonth(Number(e.target.value))}>
                  {MONTHS.slice(1).map((m,i) => (
                    <MenuItem key={i+1} value={i+1}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select value={attYear} onChange={e => setAttYear(Number(e.target.value))}>
                  {[now.getFullYear()-1, now.getFullYear()].map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>
              {!attLoading && (
                <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
                  <Chip icon={<AccessTimeIcon sx={{ fontSize: 14 }} />} label={`${fmtHHMM(totalWork)} ${t('report.workHours')}`} size="small" color="success" variant="outlined" sx={{ fontWeight: 700 }} />
                  <Chip label={`${workedDays} ${t('report.workDays')}`} size="small" sx={{ fontWeight: 700, bgcolor: BRAND_SOFT, color: BRAND_DARK }} />
                  {remoteDays > 0 && <Chip icon={<HomeWorkIcon sx={{ fontSize: 14 }} />} label={`${remoteDays} ${t('report.remote')}`} size="small" sx={{ fontWeight: 700, bgcolor: '#f5f3ff', color: '#7c3aed' }} />}
                </Stack>
              )}
            </Box>

            {attLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : attData.length === 0 ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <AccessTimeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">{t('report.noAttendanceForMonth')}</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {[t('report.date'), t('report.day'), t('report.arrival'), t('report.break'), t('report.departure'), t('report.workHours'), t('report.mode')].map(h => (
                        <TableCell key={h} sx={{ fontWeight: 700, bgcolor: BRAND_DEEP, color: '#fff !important', fontSize: '0.72rem', letterSpacing: 0.4 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {attData.map((row, i) => {
                      const d = new Date(row.date);
                      const isToday = row.date === new Date().toISOString().slice(0,10);
                      const isOpen  = !row.checkOut && row.checkIn;
                      return (
                        <TableRow key={row.id || i} hover sx={{
                          bgcolor: isToday ? 'rgba(59,130,246,0.05)' : undefined,
                          fontWeight: isToday ? 700 : undefined,
                        }}>
                          <TableCell>
                            <Typography variant="caption" fontWeight={isToday ? 800 : 600} color={isToday ? 'primary.main' : 'text.primary'}>
                              {row.date}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">{WEEK_DAYS[d.getDay()]}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" fontWeight={700} color="success.main">{fmtClock(row.checkIn)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color={row.breakMin > 0 ? 'warning.main' : 'text.disabled'}>
                              {row.breakMin > 0 ? `${row.breakMin} min` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {isOpen
                              ? <Chip label={t('report.active')} size="small" color="success" sx={{ fontSize: '0.65rem', height: 20 }} />
                              : <Typography variant="caption" fontWeight={600}>{fmtClock(row.checkOut)}</Typography>}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={700} color={row.workMin > 0 ? 'success.main' : 'text.disabled'}>
                              {row.workMin > 0 ? fmtHHMM(row.workMin) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {row.location === 'remote'
                              ? <Chip icon={<HomeWorkIcon sx={{ fontSize: 13 }} />} label={t('report.remote')} size="small" sx={{ bgcolor: '#f5f3ff', color: '#7c3aed', fontSize: '0.68rem', height: 22, fontWeight: 700 }} />
                              : <Chip label={t('report.office')} size="small" sx={{ bgcolor: BRAND_SOFT, color: BRAND_DARK, fontSize: '0.68rem', height: 22, fontWeight: 600 }} />
                            }
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        );
      })()}

      {/* ── Email Dialog ──────────────────────────────────────── */}
      <SendReportEmailDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        reportTitle={`${t('report.employeeCardTitle')} — ${fullName}`}
        subtitle={`${ec.Position || ''} · ${ec.DepartmentName || ec.Department || ''}`}
        columns={[
          { label: t('report.month'), key: s => s.ListName || MONTHS[Number(s.Month || s.month)] || `${t('report.month')} ${s.listId || s.ListId || ''}`, align: 'left' },
          { label: t('report.grossEur'), key: 'Salary', align: 'right', format: 'currency' },
          { label: t('report.taxEur'), key: 'taxes', align: 'right', format: 'currency' },
          { label: t('report.contributions'), key: 'contributions', align: 'right', format: 'currency' },
          { label: t('report.netEur'), key: 'neto', align: 'right', format: 'currency' },
          { label: t('report.bonus'), key: 'Bonus', align: 'right', format: 'currency' },
        ]}
        rows={sals}
        totals={{ Salary: totalGross, taxes: totalTaxes, contributions: totalContrib, neto: totalNeto }}
        workers={[ec]}
      />
    </Box>
  );
}
